/**
 * Cheque Payments Table Component
 * Displays cheque payment transactions
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';
import NewFeeReciept from '../../finance/newFeeReciept';

export class ChequePaymentsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chequePayments: [],
            transactions: [],
            flattenedData: [],
            originalTransactions: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: null
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind template methods
        this.snoBodyTemplate = this.snoBodyTemplate.bind(this);
        this.chequeNoBodyTemplate = this.chequeNoBodyTemplate.bind(this);
        this.chequeDateBodyTemplate = this.chequeDateBodyTemplate.bind(this);
        this.receiptDateBodyTemplate = this.receiptDateBodyTemplate.bind(this);
        this.chequeStatusBodyTemplate = this.chequeStatusBodyTemplate.bind(this);
        this.feeTypeBodyTemplate = this.feeTypeBodyTemplate.bind(this);
        this.paidAmountBodyTemplate = this.paidAmountBodyTemplate.bind(this);
        this.totalBodyTemplate = this.totalBodyTemplate.bind(this);
        this.actionsBodyTemplate = this.actionsBodyTemplate.bind(this);
        
        // Bind methods
        this.syncScrollFromData = this.syncScrollFromData.bind(this);
        this.syncScrollFromGrandTotal = this.syncScrollFromGrandTotal.bind(this);
        this.rowClassName = this.rowClassName.bind(this);
    }

    async componentDidMount() {
        console.log('ChequePaymentsTable component mounted');
        
        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false, chequePayments: [] });
        }

        // Set up scroll synchronization after a short delay to ensure DOM is ready
        setTimeout(() => {
            this.attachScrollListener();
        }, 100);
    }

    processTransactionsData = (data) => {
        console.log('ChequePayments - Processing transactions data:', data);

        const transactions = data?.collections;
        console.log('ChequePayments - Transactions from collections:', transactions);

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            // Filter for cheque payments only
            const chequePayments = transactions.filter(t =>
                t.paymentMode === 'Cheque' || t.paymentMode === 'cheque'
            );

            console.log('Filtered cheque payments:', chequePayments.length);

            // Transform and flatten the data to show fee types
            const transformedData = this.transformApiDataToTableFormat(chequePayments);
            const flattened = this.flattenTransactions(transformedData);

            console.log('Transformed data:', transformedData);
            console.log('Flattened data:', flattened);

            this.setState({
                chequePayments: chequePayments,
                transactions: transformedData,
                flattenedData: flattened,
                originalTransactions: chequePayments,
                isLoading: false
            }, () => {
                // Reattach scroll listener after state update
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.warn('No valid transactions in collections');
            this.setState({
                chequePayments: [],
                transactions: [],
                flattenedData: [],
                originalTransactions: [],
                isLoading: false
            });
        }
    }

    attachScrollListener = () => {
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        
        if (dataWrapper && grandTotalWrapper) {
            // Remove existing listeners first to avoid duplicates
            dataWrapper.removeEventListener('scroll', this.syncScrollFromData);
            grandTotalWrapper.removeEventListener('scroll', this.syncScrollFromGrandTotal);
            
            // Add new listeners - bidirectional sync
            dataWrapper.addEventListener('scroll', this.syncScrollFromData);
            grandTotalWrapper.addEventListener('scroll', this.syncScrollFromGrandTotal);
            
            console.log('ChequePayments: Scroll listeners attached to both tables');
        }
    }

    syncScrollFromData = (e) => {
        const scrollLeft = e.target.scrollLeft;
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper && grandTotalWrapper.scrollLeft !== scrollLeft) {
            grandTotalWrapper.scrollLeft = scrollLeft;
        }
    }

    syncScrollFromGrandTotal = (e) => {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper && dataWrapper.scrollLeft !== scrollLeft) {
            dataWrapper.scrollLeft = scrollLeft;
        }
    }

    flattenTransactions = (transactionGroups) => {
        const flattened = [];
        let globalSNo = 1;

        transactionGroups.forEach((group, groupIndex) => {
            const feeDetails = group.feeDetails || [];
            const totalRows = feeDetails.length;

            feeDetails.forEach((feeDetail, feeIndex) => {
                flattened.push({
                    sNo: globalSNo,
                    feeDetail: feeDetail,
                    groupIndex: groupIndex,
                    feeIndex: feeIndex,
                    isFirstRow: feeIndex === 0,
                    isLastRow: feeIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    transactionId: feeDetail.transactionId,
                    receiptNo: feeDetail.receiptNo,
                    uniqueKey: `${globalSNo}-${feeDetail.receiptNo}-${feeIndex}`,
                    originalCollection: group.originalCollection // Pass through original data
                });
            });

            globalSNo++;
        });

        return flattened;
    };

    transformApiDataToTableFormat = (collections) => {
        if (!collections || collections.length === 0) {
            return [];
        }

        const result = [];
        let sNoCounter = 1;

        collections.forEach((collection) => {
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const chequeNo = collection.chequeNo || 'N/A';
            const chequeDate = collection.chequeDate || null;
            const chequeStatus = collection.chequeStatus || 'N/A';
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            const transactionFeeDetails = [];

            // Each feePaidDetails item becomes a row in the table
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    // Handle fee type display - don't show "undefined" or empty payment term
                    let feeTypeDisplay = feeDetail.feeType || 'N/A';
                    if (feeDetail.paymentTerm && feeDetail.paymentTerm !== 'undefined') {
                        feeTypeDisplay = `${feeTypeDisplay} - ${feeDetail.paymentTerm}`;
                    }
                    
                    transactionFeeDetails.push({
                        transactionId: transactionId,
                        remarks: remarks,
                        receiptNo: receiptNo,
                        receiptDate: receiptDate,
                        transactionDate: transactionDate,
                        chequeNo: chequeNo,
                        chequeDate: chequeDate,
                        chequeStatus: chequeStatus,
                        feeType: feeTypeDisplay,
                        paidAmount: feeDetail.amountPaying || 0,
                        total: totalAmountPaid
                    });
                });
            }

            // Only add if there are fee details
            if (transactionFeeDetails.length > 0) {
                result.push({
                    sNo: sNoCounter++,
                    feeDetails: transactionFeeDetails,
                    originalCollection: collection // Store reference to original data
                });
            }
        });

        return result;
    };

    rowClassName = (rowData) => {
        if (!rowData) return '';
        
        const classes = [];
        
        // Add class for first row of a transaction (shows separator)
        if (rowData.isFirstRow && rowData.sNo > 1) {
            classes.push('transaction-start-row');
        }
        
        // Add class for last row of a transaction
        if (rowData.isLastRow) {
            classes.push('transaction-last-row');
        }
        
        return classes.join(' ');
    };

    async componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ 
                isLoading: false, 
                chequePayments: [], 
                transactions: [], 
                flattenedData: [], 
                originalTransactions: [] 
            });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listeners
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        
        if (dataWrapper) {
            dataWrapper.removeEventListener('scroll', this.syncScrollFromData);
        }
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScrollFromGrandTotal);
        }
    }

    formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Body Template Methods
    snoBodyTemplate(rowData, { rowIndex }) {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    }

    chequeNoBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail?.chequeNo || '—'}</span>;
    }

    chequeDateBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return <span>{this.formatDate(rowData.feeDetail?.chequeDate)}</span>;
    }

    receiptDateBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        const date = rowData.feeDetail?.receiptDate || rowData.feeDetail?.transactionDate;
        return <span>{this.formatDate(date)}</span>;
    }

    chequeStatusBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return <span>Realised</span>;
    }

    feeTypeBodyTemplate(rowData) {
        // Show on every row
        return <span>{rowData.feeDetail?.feeType || '—'}</span>;
    }

    paidAmountBodyTemplate(rowData) {
        // Show on every row
        const amount = rowData.feeDetail?.paidAmount || 0;
        return <span style={{ display: 'block' }}>{amount.toFixed(2)}</span>;
    }

    totalBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        const amount = rowData.feeDetail?.total || 0;
        return <span style={{ display: 'block',}}>{amount.toFixed(2)}</span>;
    }

    actionsBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return '';
        return (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <i
                    className="pi pi-eye"
                    style={{
                        fontSize: '1rem',
                        color: '#076EFF',
                        cursor: 'pointer'
                    }}
                    title="View Receipt"
                    onClick={() => {
                        console.log('Eye icon clicked for:', rowData);
                        
                        // Use the original collection data stored in the rowData
                        const originalTransaction = rowData.originalCollection;
                        
                        if (originalTransaction) {
                            console.log('Original transaction found:', originalTransaction);
                            // Pass the original transaction data directly to the receipt
                            this.setState({
                                isEnableReceipt: true,
                                receiptData: originalTransaction
                            }, () => {
                                console.log('State updated - isEnableReceipt:', this.state.isEnableReceipt);
                                console.log('State updated - receiptData:', this.state.receiptData);
                            });
                        } else {
                            console.error('Original transaction not found in rowData');
                        }
                    }}
                />
            </div>
        );
    }

    calculateGrandTotal = () => {
        return this.state.chequePayments.reduce((sum, payment) => {
            const amount = payment.amountPaid || payment.totalAmount || 0;
            return sum + amount;
        }, 0);
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        console.log('ChequePaymentsTable rendering with:', {
            flattenedDataCount: flattenedData.length,
            grandTotal: grandTotal
        });

        const tableContent = (
            <>
                {isLoading && <LoadingComponent />}

                {!isLoading && (
                    <div className="cheque-payments-table-container">
                        <div className="payment-mode-table-wrapper">
                            <div ref={this.dataScrollRef} className="data-table-scroll-container">
                                <DataTable
                                    value={flattenedData}
                                    scrollable
                                    scrollHeight="flex"
                                    className="payment-mode-table"
                                    showGridlines
                                    stripedRows
                                    emptyMessage="No cheque payments found"
                                    rowClassName={this.rowClassName}
                                >
                                    <Column
                                        header="S.No"
                                        body={this.snoBodyTemplate}
                                        style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}
                                    />
                                    <Column
                                        header="Cheque No"
                                        body={this.chequeNoBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Cheque Date"
                                        body={this.chequeDateBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Receipt Date"
                                        body={this.receiptDateBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Cheque Status"
                                        body={this.chequeStatusBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="FeeType"
                                        body={this.feeTypeBodyTemplate}
                                        style={{ width: '280px', minWidth: '280px' }}
                                    />
                                    <Column
                                        header="Paid Amount"
                                        body={this.paidAmountBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Total"
                                        body={this.totalBodyTemplate}
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={this.actionsBodyTemplate}
                                        style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}
                                    />
                                </DataTable>
                            </div>

                            <div ref={this.grandTotalScrollRef}>
                                <DataTable
                                    value={[{ label: 'Total', amount: grandTotal }]}
                                    className="payment-mode-grand-total-table"
                                    scrollable
                                    scrollHeight="flex"
                                    showGridlines
                                >
                                    <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    <Column body={() => ''} style={{ width: '280px', minWidth: '280px' }} />
                                    <Column 
                                        body={() => <strong style={{ color: '#076EFF' }}>Total</strong>} 
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} 
                                    />
                                    <Column 
                                        body={(rowData) => <strong style={{ color: '#076EFF' }}>{rowData.amount.toFixed(2)}</strong>} 
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} 
                                    />
                                    <Column body={() => ''} style={{ width: '100px', minWidth: '100px' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.isEnableReceipt && this.state.receiptData && (
                    <NewFeeReciept
                        receiptData={this.state.receiptData}
                        hideFeeReciept={() => {
                            console.log('Closing receipt');
                            this.setState({
                                isEnableReceipt: false,
                                receiptData: null
                            });
                        }}
                    />
                )}
            </>
        );

        // If showTabWrapper prop is false, return content without tab wrapper
        if (this.props.showTabWrapper === false) {
            return tableContent;
        }

        // Default: return with tab wrapper for use in View Transactions tabs
        return (
            <div className="tab-content cheque-payments-content">
                {tableContent}
            </div>
        );
    }
}
